import { useCallback, useEffect, useState } from 'react';
import { apiTokensAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import { toast } from 'sonner';
import { KeyRound, Plus, Loader2, Copy, Check, Trash2, Terminal, ChevronDown, ChevronUp } from 'lucide-react';
import { soloFecha } from '../lib/fechas';

const VIGENCIAS = [
  { dias: 7, label: '7 dias' },
  { dias: 30, label: '30 dias' },
  { dias: 90, label: '90 dias' },
  { dias: 365, label: '1 año' },
];

const COLOR_ESTADO = {
  activo: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  vencido: 'bg-amber-50 text-amber-700 border-amber-200',
  revocado: 'bg-zinc-100 text-zinc-500 border-zinc-200',
};

function fecha(iso) {
  if (!iso) return '—';
  try {
    return soloFecha(iso);
  } catch {
    return iso;
  }
}

// El instructivo se pega tal cual en Cursor. Esta escrito **para el agente**, no para la
// persona: por eso separa lo que puede resolver solo de lo que tiene que preguntar. Sin esa
// division, el primer reflejo del agente es pedir el token por chat — que es exactamente lo
// que no hay que hacer con una credencial.
function instructivo({ origin, tenant, capsule }) {
  const base = `\${OMNISYS_URL}/tenants/${tenant}/capsules/${capsule}/tickets`;
  return `# Consultar OmniSyS desde este proyecto

Necesito poder leer el contexto completo de un caso de OmniSyS —conversaciones, tareas,
avances, tiempos, workspace— sin entrar a la aplicacion. Armate una forma estable de hacerlo.

## Lo que podes resolver solo

1. Confirma que el entorno tenga las dos variables:
   \`echo "\${OMNISYS_URL:?falta OMNISYS_URL} | \${OMNISYS_TOKEN:0:12}..."\`
2. Proba la conexion con una lectura:
   \`curl -sS -H "Authorization: Bearer \${OMNISYS_TOKEN}" "${base}" | head -c 300\`
3. Arma un comando reutilizable de este proyecto que reciba un \`ticket_id\` y devuelva
   **un solo JSON** con todas las partes de abajo. Que una parte falle no puede tirar abajo
   el resto: un caso sin workspace es normal.
4. Dejalo documentado en el README, con un ejemplo de uso.

## Lo que tenes que pedirme

- Si \`OMNISYS_TOKEN\` no esta definido, pedime que lo exporte en la terminal.
  **No me pidas que te lo pegue en el chat** y no lo escribas en ningun archivo del repo
  ni en el historial de comandos.
- Si una llamada devuelve **401**, el token vencio o fue revocado: pedime uno nuevo.
  No busques otra via de acceso.
- Si algo de lo que te pido necesita **escribir** en OmniSyS, decimelo. Con este token no se
  puede y no hay que intentarlo.

## Conexion

    OMNISYS_URL   ${origin}/api
    tenant        ${tenant}
    capsula       ${capsule}

Base de los casos:

    ${base}

## Endpoints, todos GET

    /<ticket_id>                       ficha completa del caso
    /<ticket_id>/tasks                 tareas: responsable, rol, estado, horas, fechas
    /<ticket_id>/conversations         conversaciones vinculadas
    /<ticket_id>/chat-history          mensajes del caso
    /<ticket_id>/workspace/tree        arbol del workspace
    /<ticket_id>/workspace/artifacts   artefactos generados
    /<ticket_id>/time-entries          imputaciones de tiempo
    /<ticket_id>/project               fases, esfuerzo estimado vs consumido, avance
    /<ticket_id>/coordinator-runs      corridas del coordinador

Listado de casos: un GET a la base, sin \`/<ticket_id>\`.

## Limites

- **Solo lectura.** Cualquier POST, PUT, PATCH o DELETE devuelve 403. Es a proposito.
- **Una sola capsula.** Este token solo lee \`${capsule}\`; otra devuelve 403.
- El token tiene vencimiento. Cuando caduque, pedime uno nuevo.
`;
}

export default function CapsuleApiTokensPanel({ tenantId, capsuleId }) {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creando, setCreando] = useState(false);
  const [nombre, setNombre] = useState('');
  const [dias, setDias] = useState(90);
  // El token en claro existe una sola vez: mientras esta en este estado. No se vuelve a pedir
  // al servidor porque el servidor no lo tiene.
  const [recienCreado, setRecienCreado] = useState(null);
  const [copiado, setCopiado] = useState(false);
  const [aRevocar, setARevocar] = useState(null);
  const [verInstructivo, setVerInstructivo] = useState(false);
  const [copiadoInstructivo, setCopiadoInstructivo] = useState(false);

  const texto = instructivo({ origin: window.location.origin, tenant: tenantId, capsule: capsuleId });

  const copiarInstructivo = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiadoInstructivo(true);
      toast.success('Instructivo copiado: pegalo en Cursor');
      setTimeout(() => setCopiadoInstructivo(false), 2500);
    } catch {
      toast.error('No se pudo copiar; abrilo y seleccionalo a mano');
    }
  };

  const load = useCallback(async () => {
    if (!tenantId || !capsuleId) return;
    setLoading(true);
    try {
      const res = await apiTokensAPI.list(tenantId, capsuleId);
      setTokens(res.data?.tokens || []);
    } catch {
      toast.error('No se pudieron cargar los tokens');
    } finally {
      setLoading(false);
    }
  }, [tenantId, capsuleId]);

  useEffect(() => { load(); }, [load]);

  const crear = async () => {
    if (!nombre.trim()) {
      toast.error('Ponele un nombre: dentro de un mes es lo unico que dice cual revocar');
      return;
    }
    setCreando(true);
    try {
      const res = await apiTokensAPI.create(tenantId, capsuleId, { nombre: nombre.trim(), dias: Number(dias) });
      setRecienCreado(res.data);
      setNombre('');
      setCopiado(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear el token');
    } finally {
      setCreando(false);
    }
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(recienCreado.token);
      setCopiado(true);
      toast.success('Token copiado');
    } catch {
      toast.error('No se pudo copiar; seleccionalo a mano');
    }
  };

  const revocar = async () => {
    try {
      await apiTokensAPI.revoke(tenantId, capsuleId, aRevocar.token_id);
      toast.success(`Token "${aRevocar.nombre}" revocado`);
      await load();
    } catch {
      toast.error('No se pudo revocar');
    } finally {
      setARevocar(null);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="rounded-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2">
            <KeyRound className="w-4 h-4" /> Tokens de API
          </CardTitle>
          <p className="text-[11px] text-zinc-500 mt-1">
            Para leer datos de esta cápsula desde afuera —un editor, un script, un tablero— sin
            poner la contraseña de nadie en un archivo. Son de <strong>solo lectura</strong> y
            quedan acotados a <strong>{capsuleId || 'esta cápsula'}</strong>.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-7">
              <Label className="text-[10px] uppercase text-zinc-500">Nombre</Label>
              <Input
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="cursor de Diego, tablero de dirección…"
                className="rounded-sm text-xs h-8 mt-1"
              />
            </div>
            <div className="col-span-3">
              <Label className="text-[10px] uppercase text-zinc-500">Vigencia</Label>
              <select
                className="w-full border border-brand-border rounded-sm p-1.5 text-xs h-8 mt-1"
                value={dias}
                onChange={(e) => setDias(e.target.value)}
              >
                {VIGENCIAS.map((v) => (
                  <option key={v.dias} value={v.dias}>{v.label}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <Button onClick={crear} disabled={creando} className="w-full rounded-sm text-xs h-8">
                {creando ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Plus className="w-3 h-3 mr-1" /> Crear</>}
              </Button>
            </div>
          </div>

          {recienCreado && (
            <div className="border border-emerald-200 bg-emerald-50/60 rounded-sm p-3 space-y-2">
              <p className="text-xs font-semibold text-emerald-900">
                Copialo ahora: no se vuelve a mostrar.
              </p>
              <p className="text-[11px] text-emerald-800">
                Del lado del servidor solo queda un hash. Si lo perdés, revocá este y creá otro.
              </p>
              <div className="flex gap-2">
                <code className="flex-1 bg-white border border-emerald-200 rounded-sm px-2 py-1.5 text-[11px] font-mono break-all">
                  {recienCreado.token}
                </code>
                <Button onClick={copiar} variant="outline" className="rounded-sm text-xs h-8 shrink-0">
                  {copiado ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                </Button>
              </div>
              <div className="text-[11px] text-emerald-900 bg-white border border-emerald-200 rounded-sm p-2 font-mono whitespace-pre-wrap break-all">
                {`export OMNISYS_URL=${window.location.origin}/api\nexport OMNISYS_TOKEN=${recienCreado.token}\npython3 scripts/contexto_de_ticket.py <ticket_id>`}
              </div>
              <Button variant="ghost" onClick={() => setRecienCreado(null)} className="text-xs h-7">
                Ya lo guardé
              </Button>
            </div>
          )}

          <div className="border border-brand-border rounded-sm divide-y">
            {loading && <p className="text-xs text-zinc-400 p-3">Cargando…</p>}
            {!loading && tokens.length === 0 && (
              <p className="text-xs text-zinc-400 p-4 text-center">
                Todavía no hay tokens en esta cápsula.
              </p>
            )}
            {tokens.map((t) => (
              <div key={t.token_id} className="flex items-center gap-3 p-2.5 text-xs">
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{t.nombre}</p>
                  <p className="text-[10px] text-zinc-500 font-mono">{t.pista}…</p>
                </div>
                <Badge variant="outline" className={`text-[10px] rounded-sm ${COLOR_ESTADO[t.estado] || ''}`}>
                  {t.estado}
                </Badge>
                <div className="text-[10px] text-zinc-500 w-56 shrink-0">
                  <div>vence {fecha(t.expires_at)}</div>
                  <div>último uso: {t.last_used_at ? fecha(t.last_used_at) : 'nunca'}</div>
                </div>
                <div className="text-[10px] text-zinc-400 w-44 truncate shrink-0">{t.creado_por_email}</div>
                {t.estado === 'activo' && (
                  <Button
                    variant="ghost"
                    onClick={() => setARevocar(t)}
                    className="h-7 w-7 p-0 text-zinc-400 hover:text-red-600 shrink-0"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2">
            <Terminal className="w-4 h-4" /> Instructivo para Cursor
          </CardTitle>
          <p className="text-[11px] text-zinc-500 mt-1">
            Pegalo en Cursor y se configura solo. Está escrito para el agente: le dice qué puede
            resolver por su cuenta y qué tiene que pedirte —empezando por el token, que no debe
            pedirte por chat ni escribir en ningún archivo.
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex gap-2">
            <Button onClick={copiarInstructivo} className="rounded-sm text-xs h-8">
              {copiadoInstructivo ? <><Check className="w-3 h-3 mr-1" /> Copiado</> : <><Copy className="w-3 h-3 mr-1" /> Copiar instructivo</>}
            </Button>
            <Button
              variant="outline"
              onClick={() => setVerInstructivo((v) => !v)}
              className="rounded-sm text-xs h-8"
            >
              {verInstructivo ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
              {verInstructivo ? 'Ocultar' : 'Ver'}
            </Button>
          </div>
          <p className="text-[11px] text-zinc-500">
            Antes de pegarlo, exportá las dos variables en la terminal donde corre Cursor:
          </p>
          <pre className="bg-zinc-900 text-zinc-100 rounded-sm p-2.5 text-[11px] font-mono overflow-x-auto">
{`export OMNISYS_URL=${window.location.origin}/api
export OMNISYS_TOKEN=omn_...`}
          </pre>
          {verInstructivo && (
            <pre className="border border-brand-border rounded-sm p-3 text-[11px] font-mono whitespace-pre-wrap max-h-[420px] overflow-y-auto bg-zinc-50">
              {texto}
            </pre>
          )}
        </CardContent>
      </Card>

      <ConfirmActionDialog
        open={!!aRevocar}
        onOpenChange={(v) => !v && setARevocar(null)}
        title="Revocar token"
        description={
          aRevocar
            ? `"${aRevocar.nombre}" deja de funcionar en el acto. Lo que lo esté usando va a empezar a recibir 401.`
            : ''
        }
        confirmLabel="Revocar"
        onConfirm={revocar}
      />
    </div>
  );
}
